import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import Layout from '../components/UI/Layout/Layout';
import ConventionsList from '../components/Conventions/ConventionsList';
import ConventionsForm from '../components/Conventions/ConventionsForm';
import { loadConventions } from '~/components/Conventions/actions';
import { LoadingTopBar } from '~/components/UI/LoadingTopBar';
import { LocalCacheHandler } from '~/utils/local-cache-handler';
import { CACHE_ACTIONS } from '~/constants/cache-actions.constants';
import usePersistedFilters from '~/_shared/hooks/usePersistedFilters';
import {
  CONVENTIONS_FILTERS_KEY,
  getDefaultConventionsFilters,
  getConventionsRequest,
  isSameConventionsRequest,
} from '~/components/Conventions/filters';

export const ScreensConventions = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const [isLoading, setIsLoading] = useState(true);
  const [drawer, setDrawer] = useState({
    isVisible: false,
    data: null,
  });

  // Year kept across refreshes for a few minutes (per tenant/user/page).
  const persisted = usePersistedFilters(CONVENTIONS_FILTERS_KEY, getDefaultConventionsFilters);
  const applied = persisted.filters;
  const isFirstLoad = useRef(true);

  // Loads the restored (or default) year on mount and on every year change,
  // always through /conventions/year/:year (same as the year Select did).
  useEffect(() => {
    loadData();
  }, [applied.year]);

  async function loadData() {
    const firstLoad = isFirstLoad.current;
    isFirstLoad.current = false;
    // The IndexedDB cache is keyed by route only, so it only ever holds the
    // default (current) year.
    const usesCache = isSameConventionsRequest(applied, persisted.defaults);

    setIsLoading(true);
    try {
      if (firstLoad && usesCache) {
        await LocalCacheHandler.load({
          actionName: CACHE_ACTIONS.conventions,
          dispatch,
          pathname
        });
      }
      const { payload } = await dispatch(
        loadConventions(Object.assign({ showLoading: false }, getConventionsRequest(applied)))
      );
      if (usesCache) {
        await LocalCacheHandler.save({
          pathname,
          data: payload.data,
        });
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }

  function hideDrawer() {
    setDrawer({
      isVisible: false,
      data: null,
    });
  }

  function showDrawer(data = null) {
    setDrawer({
      isVisible: true,
      data,
    });
  }

  return (
    <div>
      <LoadingTopBar isActive={isLoading} />
      <Layout>
        <ConventionsList
          showEdit={showDrawer}
          showModal={() => showDrawer()}
          persisted={persisted}
        />
        <ConventionsForm
          visible={drawer.isVisible}
          onClose={hideDrawer}
          data={drawer.data}
        />
      </Layout>
    </div>
  );
};
